import ConfirmationNumberOutlinedIcon from '@offline-mui/icons-material/ConfirmationNumberOutlined'
import ContentCopyIcon from '@offline-mui/icons-material/ContentCopy'
import DeleteOutlinedIcon from '@offline-mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@offline-mui/icons-material/EditOutlined'
import ExpandMoreIcon from '@offline-mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@offline-mui/material'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { cloneInventory, deleteInventory } from '../api/inventory'
import type { InventoryItinerary, InventorySegment } from '../api/inventory'
import {
  fareTotal,
  formatMoney,
  formatShortDate,
  formatTicketBy,
  inboundSegment,
  outboundSegment,
  routeSummary,
  segmentFlights,
} from '../utils/flightFormat'
import ItineraryDirectionCard from './ItineraryDirectionCard'
// import PriceBreakdownPopover from './PriceBreakdownPopover'
import CarrierBadge from './ui/CarrierBadge'

type ItineraryResultCardProps = {
  itinerary: InventoryItinerary
  onCloned?: (itinerary: InventoryItinerary) => void
  onDeleted?: (id: number) => void
}

/* ------------------------------------------------------------------ */
/* Summary row cells                                                   */
/* ------------------------------------------------------------------ */

function CellLabel({ children }: { children: string }) {
  return (
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mb: 0.35,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  )
}

function stopsLabel(connections: number) {
  if (!connections) return 'Nonstop'
  return `${connections} stop${connections === 1 ? '' : 's'}`
}

function RouteCell({
  label,
  segment,
}: {
  label: string
  segment: InventorySegment | null
}) {
  if (!segment) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <CellLabel>{label}</CellLabel>
        <Typography sx={{ color: 'text.disabled', fontWeight: 500 }}>One way</Typography>
      </Box>
    )
  }
  const route = routeSummary(segment)
  const flights = segmentFlights(segment)
  const departureDate = formatShortDate(flights[0]?.departure_datetime)

  return (
    <Box sx={{ minWidth: 0 }}>
      <CellLabel>{label}</CellLabel>
      <Typography
        sx={{
          fontWeight: 700,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1.3,
        }}
      >
        {route.origin}
        <Box component="span" sx={{ color: 'text.disabled', mx: 0.6, fontWeight: 500 }}>
          →
        </Box>
        {route.destination}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {departureDate} · {stopsLabel(route.connections)}
      </Typography>
    </Box>
  )
}

function MetaCell({ label, value }: { label: string; value: string | null }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <CellLabel>{label}</CellLabel>
      <Typography
        sx={{
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: value ? 'text.primary' : 'text.disabled',
          lineHeight: 1.3,
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  )
}

function SeatsCell({ booked, blocked }: { booked: number; blocked: number }) {
  const percent = blocked > 0 ? Math.min(100, Math.round((booked / blocked) * 100)) : 0
  return (
    <Box sx={{ minWidth: 0 }}>
      <CellLabel>Seats</CellLabel>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <LinearProgress
          variant="determinate"
          value={percent}
          aria-label={`${booked} of ${blocked} seats booked`}
          sx={{ flexGrow: 1, minWidth: 48 }}
        />
        <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {booked}
          <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500 }}>
            {' / '}
            {blocked}
          </Box>
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {percent}% booked
      </Typography>
    </Box>
  )
}

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

export default function ItineraryResultCard({
  itinerary,
  onCloned,
  onDeleted,
}: ItineraryResultCardProps) {
  // const [priceAnchor, setPriceAnchor] = useState<HTMLElement | null>(null)
  const [confirmingClone, setConfirmingClone] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [cloneError, setCloneError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const canDelete = itinerary.booking_count === 0
  const active = itinerary.is_active === true

  const outbound = outboundSegment(itinerary)
  const inbound = inboundSegment(itinerary)
  const fare = itinerary.fares?.[0]
  const total = fareTotal(fare)
  const paxLabel = fare?.passenger_type ? `per ${fare.passenger_type.toLowerCase()}` : 'fare total'

  return (
    <>
      <Accordion
        sx={{
          border: '2px solid',
          borderColor: active ? 'success.main' : 'error.main',
          borderRadius: 1.5,
          mb: 1.25,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          sx={{
            px: { xs: 2, md: 2.5 },
            minHeight: 72,
            '& .MuiAccordionSummary-content': {
              my: 1.75,
              minWidth: 0,
              flexGrow: 1,
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'grid',
              alignItems: 'center',
              columnGap: { xs: 2, lg: 3 },
              rowGap: 1.5,
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: 'minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1fr) auto',
                lg: 'minmax(170px, 1.35fr) minmax(130px, 1fr) minmax(130px, 1fr) minmax(120px, 0.9fr) minmax(120px, 0.9fr) minmax(140px, 1.05fr) auto',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: 'center', minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' } }}
            >
              <Stack spacing={0.35} sx={{ alignItems: 'center', flexShrink: 0 }}>
                <CarrierBadge code={itinerary.validating_carrier || itinerary.plating_carrier} />
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 700, lineHeight: 1 }}
                >
                  #{itinerary.id}
                </Typography>
              </Stack>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontWeight: 700,
                    fontSize: 18,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {formatMoney(total)}
                </Typography>
                {/*
                  Price breakdown on hover:
                  role="button" tabIndex={0} aria-label="Show price breakdown"
                  onMouseEnter / onClick / onFocus set priceAnchor to event.currentTarget
                */}
                <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {paxLabel}
                </Typography>
              </Box>
            </Stack>

            <RouteCell label="Outbound" segment={outbound} />
            <RouteCell label="Inbound" segment={inbound} />
            <MetaCell label="Ticket by" value={formatTicketBy(itinerary.last_ticketable_date)} />
            <MetaCell
              label="Seat release"
              value={itinerary.seat_release_date ? formatTicketBy(itinerary.seat_release_date) : null}
            />
            <SeatsCell
              booked={Number(itinerary.total_seats_booked) || 0}
              blocked={Number(itinerary.total_seats_blocked) || 0}
            />

            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexShrink: 0,
                gridColumn: { xs: '1 / -1', md: '4', lg: 'auto' },
                gridRow: { md: '1', lg: 'auto' },
              }}
            >
            <Stack
              direction="row"
              spacing={0.25}
              sx={{ alignItems: 'center' }}
              onClick={(event) => event.stopPropagation()}
              onFocus={(event) => event.stopPropagation()}
            >
              <Tooltip title="Edit block">
                <IconButton
                  size="small"
                  aria-label="Edit block"
                  component={RouterLink}
                  to={`/offline-flight/create?id=${itinerary.id}`}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clone">
                <IconButton
                  size="small"
                  aria-label="Clone"
                  disabled={cloning}
                  onClick={() => {
                    setCloneError(null)
                    setConfirmingClone(true)
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="View bookings">
                <IconButton
                  size="small"
                  aria-label="View bookings"
                  component={RouterLink}
                  to={`/offline-flight/bookings?itineraryId=${itinerary.id}`}
                >
                  <ConfirmationNumberOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canDelete ? (
                <Tooltip title="Delete block">
                  <IconButton
                    size="small"
                    aria-label="Delete block"
                    disabled={deleting}
                    onClick={() => {
                      setDeleteError(null)
                      setConfirmingDelete(true)
                    }}
                  >
                    <DeleteOutlinedIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
              <ExpandMoreIcon
                sx={{
                  flexShrink: 0,
                  ml: 1,
                  color: 'action.active',
                  transition: 'transform .2s',
                  '.Mui-expanded &': { transform: 'rotate(180deg)' },
                }}
              />
            </Stack>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{ p: 0, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
        >
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Grid container spacing={2}>
              {outbound ? (
                <Grid size={{ xs: 12, xl: inbound ? 6 : 12 }}>
                  <ItineraryDirectionCard
                    itinerary={itinerary}
                    segment={outbound}
                    direction="Outbound"
                  />
                </Grid>
              ) : null}
              {inbound ? (
                <Grid size={{ xs: 12, xl: 6 }}>
                  <ItineraryDirectionCard
                    itinerary={itinerary}
                    segment={inbound}
                    direction="Inbound"
                  />
                </Grid>
              ) : null}
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/*
      <PriceBreakdownPopover
        itinerary={itinerary}
        anchorEl={priceAnchor}
        onClose={() => setPriceAnchor(null)}
      />
      */}

      <Dialog
        open={confirmingClone}
        onClose={cloning ? undefined : () => setConfirmingClone(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Clone block #{itinerary.id}?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography>
              This creates a new block with the same flights, fares, and taxes. Bookings stay on
              the original block. The new block starts inactive, with no seats booked.
            </Typography>
            {cloneError ? <Alert severity="error">{cloneError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmingClone(false)} disabled={cloning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={cloning}
            onClick={() => {
              setCloning(true)
              setCloneError(null)
              cloneInventory(itinerary.id)
                .then((result) => {
                  setConfirmingClone(false)
                  onCloned?.(result.data)
                })
                .catch((err: unknown) => {
                  setCloneError(err instanceof Error ? err.message : 'Clone failed')
                })
                .finally(() => setCloning(false))
            }}
          >
            {cloning ? 'Cloning…' : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmingDelete}
        onClose={deleting ? undefined : () => setConfirmingDelete(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete block #{itinerary.id}?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography>
              This block has no bookings. Deleting it removes the block and its flights, fares,
              and other related rows. Saved bookings are left in place.
            </Typography>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmingDelete(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={() => {
              setDeleting(true)
              setDeleteError(null)
              deleteInventory(itinerary.id)
                .then(() => {
                  setConfirmingDelete(false)
                  onDeleted?.(itinerary.id)
                })
                .catch((err: unknown) => {
                  setDeleteError(err instanceof Error ? err.message : 'Delete failed')
                })
                .finally(() => setDeleting(false))
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
